import {inject, Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {
  CONVERSATION_STATUS,
  CONVERSATION_SEARCH_TYPE,
  CONVERSATION_TYPE,
  IConversation,
  IConversationFilterParam,
  IConversationFilterState,
  IConversationListResponse,
  ISearchMessage,
  ISearchResults,
} from '../models/conversation.model';
import {matchRealtimeConversationFilter, REALTIME_FILTER_MATCH} from '../utils/realtime-list-filter.util';
import {EConversationAction, IConversationActionEvent} from '../models/conversation-action.model';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {AuthService} from '@core/authentication/auth.service';
import {ROLES} from '@shared/models/user';
import {IResponseApi} from '@core/models';
import {Title} from '@angular/platform-browser';
import {ConversationInteractionService} from './conversation-interaction.service';
import {ConversationRealtimeService} from './conversation-realtime.service';
import {QuickMessageService} from './quick-message.service';
import {TagService} from './tag.service';
import {ConversationPageSettingsService} from './conversation-page-settings.service';
import {TAG_SETTING_PAYLOAD_TYPE} from '@shared/models/realtime/do-event.model';
import {MuteConversationService} from './mute-conversation.service';
import {LAST_MESSAGE_LABEL, MuteTypeValue, ZALO_PERSONAL_CONST_GROUP_EVENT, ZALO_PERSONAL_GROUP_EVENT} from '@features/conversation/models/conversation-constants.model';
import {SplitConversationService} from './split-conversation.service';
import {ConversationFilterService} from './conversation-filter.service';
import {ConversationSoundService} from './conversation-sound.service';
import {LoggerService} from '@shared/services/global/logger.service';
import {UserSettingsService} from '@shared/services/global/user-settings.service';
import {CustomerSyncService} from './customer-sync.service';
import {ConversationSearchService} from './conversation-search.service';
import {BusinessCallingService} from './business-calling.service';
import {ZaloPersonalService} from '@features/conversation/services/zalo-personal.service';
import {CHANNEL} from '@features/conversation/models/channel.model';

/**
 * Cấu trúc message thô từ DO event payload — BE gửi flat fields
 * (message, senderId, createdAt), không nested như IChatMessage.
 */
interface IDoRawMessage {
  id?: string | number;
  message?: string;
  content?: string;
  senderId?: string | number;
  createdAt?: number;
  updatedTime?: number;
  attachments?: Array<{ type?: string; payload?: {type?: string | number; [key: string]: unknown}; [key: string]: unknown }>;
}

const ZLPS_GROUP_EVENT_TYPES = new Set([
  ...Object.values(ZALO_PERSONAL_GROUP_EVENT),
  ...Object.values(ZALO_PERSONAL_CONST_GROUP_EVENT),
].map(String));

/**
 * Enum kiểu sắp xếp danh sách hội thoại.
 * Tương ứng với cài đặt user "sortActivities" từ bản cũ:
 * 1 (mặc định) = UNREAD_FIRST
 * 2 = BY_TIME
 * 3 = NO_REPLY_FIRST
 */
export enum ESortMode {
  UNREAD_FIRST = 1, // Pin → Chưa đọc trước → Thời gian
  BY_TIME = 2, // Pin → Thời gian mới nhất
  NO_REPLY_FIRST = 3 // Pin → Chưa trả lời trước → Thời gian
}


/**
 * Quản lý danh sách hội thoại hiển thị ở cột trái:
 * - State items$ để UI subscribe
 * - Phân trang cursor-based (IResDataElasticDefault)
 * - Sắp xếp 3 kiểu (unread/time/noreply)
 * - Thực thi các hành động (pin/mute/assign...) qua executeAction
 * - Đếm alertUnread và cập nhật title tab trình duyệt
 */
@Injectable({providedIn: 'root'})
export class ConversationListService implements OnDestroy {
  private _items$ = new BehaviorSubject<IConversation[]>([]);
  /** Observable chứa danh sách hội thoại hiện tại */
  public items$ = this._items$.asObservable();

  private _isLoading$ = new BehaviorSubject<boolean>(false);
  /** Observable trạng thái đang tải dữ liệu */
  public isLoading$ = this._isLoading$.asObservable();

  /** Tổng số hội thoại khớp bộ lọc hiện tại. `null` khi BE không trả tổng hoặc đang tải lại. */
  private _filteredResultCount$ = new BehaviorSubject<number | null>(null);
  public filteredResultCount$ = this._filteredResultCount$.asObservable();

  /** Filter của lần tải danh sách gần nhất, dùng để khôi phục Inbox mà không gọi lại API. */
  private lastFetchedParamsSignature: string | null = null;

  /** Số hội thoại chưa đọc hiện tại — dùng để cập nhật title tab */
  private _alertUnread$ = new BehaviorSubject<number>(0);
  public alertUnread$ = this._alertUnread$.asObservable();

  /** Base title gốc (không có prefix unread) — dùng để ghi đè title, không cộng dồn */
  private _baseTitle: string | null = null;

  /** Cursor phân trang cho hội thoại chính */
  private _nextPageCursor: any[] | null = null;
  /** Cursor phân trang cho hội thoại affiliate */
  private _nextAffCursor: any[] | null = null;
  /** Cancels stale one-page scans that bring pinned conversations to the initial list. */
  private pinnedConversationScanToken = 0;

  /** Kết quả tìm kiếm — null khi không đang search (3.B.9) */
  readonly searchService = inject(ConversationSearchService);

  /** Kiểu sort hiện tại (đọc từ user setting) */
  private _sortMode: ESortMode = ESortMode.UNREAD_FIRST;
  /** Mã request danh sách mới nhất: response của lần lọc trước về muộn thì bỏ qua. */
  private listRequestSeq = 0;

  /** Set ID hội thoại đang sync tên — tránh gọi trùng lặp */
  private readonly customerSyncSvc = inject(CustomerSyncService);
  private readonly zaloPersonal = inject(ZaloPersonalService);

  /** Số item mỗi trang (từ user setting display) */
  private _limit = 250;

  /** Bridge realtime DO → list mutations. */
  private readonly realtime = inject(ConversationRealtimeService);
  /** QuickMessage service — nhận DO event để cập nhật cache (giống tags). */
  private readonly quickMsgSvc = inject(QuickMessageService);
  /** Tag service — nhận DO event khi Cài đặt nhãn thay đổi để cập nhật danh sách nhãn. */
  private readonly tagService = inject(TagService);
  /** Cache cài đặt page — Cài đặt nhãn đổi hiển thị gợi ý nhãn thì cập nhật tại chỗ. */
  private readonly pageSettings = inject(ConversationPageSettingsService);
  /** Service xử lý mute (đồng bộ timer tự bật lại thông báo). */
  private readonly muteService = inject(MuteConversationService);
  /** Service quản lý chia hội thoại — populate split data từ API response. */
  private readonly splitService = inject(SplitConversationService);
  /** Service quản lý filter params hiện tại (để check new conversation filter). */
  private readonly filterService = inject(ConversationFilterService);
  /** Service phát âm thanh thông báo. */
  private readonly soundService = inject(ConversationSoundService);
  /** User settings service — đọc display limit. */
  private readonly userSettings = inject(UserSettingsService);
  /** Logger trung tâm — tự động tắt log ở production. */
  private readonly logger = inject(LoggerService);
  private readonly callingService = inject(BusinessCallingService);
  /**
   * Quyền quản lý hội thoại (Manager / Admin / SuperAdmin).
   * Chuẩn theo FE13: authService.isAllowed(ROLES.Business_Manager) || authService.isAdmin().
   */
  private get limitPermission(): boolean {
    return this.authService.isAllowed(ROLES.Business_Manager) || this.authService.isAdmin();
  }

  private readonly _destroy$ = new Subject<void>();
  /** Timer tự tắt trạng thái typing của Zalo cá nhân (BE không gửi stop-typing). */
  private readonly zaloPersonalTypingTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private api: ConversationApiService,
    private authService: AuthService,
    private titleService: Title,
    private interactionService: ConversationInteractionService
  ) {
    this.logger.debug('[DEBUG-CONV] ConversationListService constructor');
    // Lắng nghe tín hiệu "Đã đọc" từ InteractionService
    this.interactionService.conversationRead$.subscribe(conversationId => {
      this.updateConversationInList(conversationId, {
        countUnread: 0,
        status: CONVERSATION_STATUS.READED
      });
      this.recountUnread();
    });

    this.bindRealtimeStreams();
    this.bindCallingRealtimeStream();
    this.bindMuteExpiry();

    // Như v1: dùng kiểu sắp xếp đã lưu khi đăng nhập ngay từ đầu. Request danh sách đi trước
    // khi /user/setting tải xong vẫn đúng thứ tự; settings$ sẽ cập nhật lại nếu khác.
    this.seedSortModeFromAuth();

    // Khi auth thay đổi (chọn page, đổi business) → reset danh sách, lần sau fetchList() sẽ tải lại
    this.authService.onAuthChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.resetState();
      this.seedSortModeFromAuth();
    });
  }

  ngOnDestroy(): void {
    this.zaloPersonalTypingTimers.forEach(timer => clearTimeout(timer));
    this.zaloPersonalTypingTimers.clear();
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Khi `MuteConversationService` tự động hết thời gian mute (timer 1h/4h/8AM),
   * service phát event `expired$` → cập nhật lại item trong list để gỡ cờ `isMuted`.
   */
  private bindMuteExpiry(): void {
    this.muteService.expired$.subscribe(evt => {
      this.updateConversationInList(evt.conversationId, {
        isMuted: false,
        muteType: undefined,
        muteUntilTimestamp: undefined
      });
    });
  }

  private bindCallingRealtimeStream(): void {
    this.realtime.calling$
      .pipe(takeUntil(this._destroy$))
      .subscribe(evt => {
        void this.callingService.handleRealtimeCallingData(evt.data);
      });
  }

  /**
   * Gắn stream realtime DO vào các thao tác trên danh sách hội thoại.
   *
   * Port đầy đủ từ FE13 `handleDataChanges()` — xử lý:
   * 1. Split conversation filter (hasSplitPage + hasAllowAssignee)
   * 2. Manager filter (divisionType/assigneeIds/assignedGroupId → hasRemoveRecordFilter)
   * 3. Merge đầy đủ fields: lastMessage, lastSeenMessageTime, lastSentMessageTime, ...
   * 4. Tag merge (tagId toggle, ops.addTagsId, ops.removeTagsId)
   * 5. Status + alertUnread count + browser title update
   * 6. assigneeIds, assignedGroupId
   * 7. topicItem (Telegram topic) merge
   * 8. hasReply update
   * 9. updatedTime
   * 10. New conversation filter (matchRealtimeConversationFilter) — chưa đủ dữ liệu thì bỏ qua
   * 11. playAlert sound notification
   * 12. Mute check → scheduleAutoUnmute
   * 13. Guest upgrade handling (remove old guest conversation)
   */

  /** Đồng bộ thay đổi DO vào danh sách, giữ dữ liệu cũ khi payload không gửi field. */
  private bindRealtimeStreams(): void {
    this.logger.debug('[DEBUG-CONV] bindRealtimeStreams() called');
    this.realtime.conversationChanges$.subscribe(evt => {
      this.logger.debug('[DEBUG-CONV] Subscriber FIRED, evt.data keys:', Object.keys(evt?.data || {}));
      try {
        this.logger.debug("[STEP-1] inside try");
        const payload = evt.data as Record<string, unknown> | undefined;
        if (!payload) {
          this.logger.debug("[STEP-2] payload falsy");
          return;
        }

        // ─── Extracted conversation + messages ──────────────────────────────
        const conversation = payload['conversation'] as Partial<IConversation> | undefined;
        // BE dùng không đồng nhất: controller gửi `messages` (mảng), service
        // layer gửi `message` (một object). Trước đây chỉ nhận mảng nên event
        // dạng `message` không đi vào nhánh playAlert trên production.
        const rawMessages = (payload['messages'] ?? payload['message']) as unknown;
        const messages: IDoRawMessage[] = Array.isArray(rawMessages)
          ? rawMessages as IDoRawMessage[]
          : rawMessages && typeof rawMessages === 'object'
            ? [rawMessages as IDoRawMessage]
            : [];

        if (!conversation?.id) {
          this.logger.debug("[STEP-4] no conv id");
          return;
        }

        this.logger.debug("[STEP-5] items len:", this._items$.getValue().length);
        const currentItems = this._items$.getValue();
        // [FIX] DO event gửi `id` dạng number, item trong list là string → === thất bại
        const idx = currentItems.findIndex(c => String(c.id) === String(conversation.id));
        this.logger.debug("[STEP-6] idx:", idx);
        const existing = idx >= 0 ? currentItems[idx] : null;
        let shouldReorder = !existing;
        const currentConversation = {...existing, ...conversation};
        if (Number(currentConversation.channel) === CHANNEL.ZALO_PERSONAL
          && Number(currentConversation.type) === CONVERSATION_TYPE.GROUP) {
          const pageId = String(currentConversation.fanpageId || '');
          const groupId = String(currentConversation.fanpageUserId || '');
          this.zaloPersonal.rememberGroupSenders(pageId, groupId, messages.slice().reverse()
            .filter(message => !message.attachments?.some(attachment => attachment.type === 'emote'))
            .map(message => String(message.senderId || ''))
            .filter(id => id && id !== pageId));
          if (messages.some(message => message.attachments?.some(attachment =>
            attachment.type === 'emote' && ZLPS_GROUP_EVENT_TYPES.has(String(attachment.payload?.['type'])),
          ))) this.zaloPersonal.invalidateGroupPreviewFromEvent(pageId, groupId);
        }

        // ─── Friend request handling (FE13: handleFriendEvent) ──────────────
        // Snackbar + sound — port từ FE13, do component show snackbar
        if (Object.prototype.hasOwnProperty.call(payload, 'friendRequest')) {
          this.interactionService.emitEvent('friendRequest', payload['friendRequest']);
        }

        // ==================================================================
        // BLOCK A: Hội thoại đã có trong danh sách
        // ==================================================================
        if (existing) {
          this.logger.debug('[DEBUG-CONV] Entered BLOCK A');
          this.logger.debug('[BLOCK-A] limitPermission:', this.limitPermission, 'assigneeIds:', conversation.assigneeIds);
          const updated = {...existing};

          // ── A1: Check split conversation (non-manager) ──────────────────
          // FE13: !limitPermission → hasSplitPage + hasAllowAssignee
          // Nếu không được assign (hoặc cờ all_staff_view bị quản lý gỡ bỏ) → xóa khỏi danh sách
          if (!this.limitPermission) {
            // [FIX CRUCIAL] Chuẩn bị object convForSplitCheck kết hợp dữ liệu existing và socket payload:
            // Socket payload có thể thiếu fanpageId hoặc assignedGroupId; ta kế thừa từ existing để hasSplitPage và hasAllowAssignee kiểm tra đúng
            const convForSplitCheck = {
              ...conversation,
              fanpageId: conversation.fanpageId || existing.fanpageId,
              assignedGroupId: conversation.assignedGroupId !== undefined ? conversation.assignedGroupId : existing.assignedGroupId,
              ops: (conversation as any)?.ops ?? (existing as any)?.ops,
            };

            const hasSplit = this.splitService.hasSplitPage(convForSplitCheck as any);
            if (hasSplit) {
              const hasAllow = this.splitService.hasAllowAssignee(convForSplitCheck as any);
              if (!hasAllow) {
                // Xoá khỏi danh sách vì user không còn được assign hội thoại này (hoặc quản lý đã xóa cờ all_staff_view)
                this.removeConversation(conversation.id);
                // Phát event qua interactionService để ChatCore reset conversation đang mở (nếu đang xem) và hiển thị dialog cảnh báo
                this.interactionService.emitEvent('conversationBlockedOrRemoved', {
                  conversationId: String(conversation.id),
                  reason: 'unassigned'
                });
                return;
              }
            }
          } else {
            // ── A2: Check manager filter (division) ──────────────────────
            // FE13: limitPermission + params.divisionType/assigneeIds/assignedGroupId
            const params = this.filterService.getCurrent();
            if (params['divisionType'] || params.assigneeIds?.length || params.assignedGroupId) {
              const convForManagerCheck = {
                ...conversation,
                fanpageId: conversation.fanpageId || existing.fanpageId,
                assigneeIds: Object.prototype.hasOwnProperty.call(conversation, 'assigneeIds')
                  ? conversation.assigneeIds
                  : existing.assigneeIds,
              };
              const shouldRemove = this.splitService.hasRemoveRecordFilter(
                convForManagerCheck as any,
                params as any,
              );
              if (shouldRemove) {
                this.logger.debug('[EARLY-RETURN] A2: shouldRemove');
                this.removeConversation(conversation.id);
                this.interactionService.emitEvent('conversationBlockedOrRemoved', {
                  conversationId: String(conversation.id),
                  reason: 'filtered_out'
                });
                return;
              }
            }
          }

          // ── A3: Merge các field timestamp ──────────────────────────────
          // FE13 lines 1475-1482
          // [FIX] Dùng hasOwnProperty thay truthy check cho các field có thể là
          // null/0/falsy nhưng vẫn cần merge (VD: chatbotStatus = null → clear status).
          const hasA3 = (k: string) => Object.prototype.hasOwnProperty.call(conversation, k);
          // Snapshot trình duyệt không ghi đè nguồn quảng cáo của hội thoại đã có.
          if (payload['fromClient'] !== true) {
            // Payload rỗng không phải lệnh xóa nguồn quảng cáo.
            const fromAdsId: unknown = conversation.fromAdsId;
            if ((typeof fromAdsId === 'string' && fromAdsId.trim() !== '')
              || (typeof fromAdsId === 'number' && Number.isFinite(fromAdsId) && fromAdsId > 0)) {
              updated.fromAdsId = conversation.fromAdsId;
            }
            if (hasA3('adIds') && Array.isArray(conversation.adIds) && conversation.adIds.length > 0) {
              updated.adIds = [...conversation.adIds];
            }
          }
          if (hasA3('lastMessage')) updated.lastMessage = conversation.lastMessage;
          if (hasA3('message')) updated.message = conversation.message;
          if (hasA3('lastMessageId')) updated.lastMessageId = conversation.lastMessageId;
          if (hasA3('lastSeenMessageTime')) updated.lastSeenMessageTime = conversation.lastSeenMessageTime;
          if (hasA3('lastSentMessageTime')) updated.lastSentMessageTime = conversation.lastSentMessageTime;
          if (hasA3('lastPageReplyAt')) updated.lastPageReplyAt = conversation.lastPageReplyAt;
          if (hasA3('lastPageRepliedAt')) updated.lastPageRepliedAt = conversation.lastPageRepliedAt;
          if (hasA3('lastUserRepliedAt')) updated.lastUserRepliedAt = conversation.lastUserRepliedAt;
          if (hasA3('lastUserSeenAt')) updated.lastUserSeenAt = conversation.lastUserSeenAt;
          if (hasA3('chatbotStatus')) updated.chatbotStatus = conversation.chatbotStatus;
          if (hasA3('isPinned')) {
            updated.isPinned = this.toBooleanFlag((conversation as Record<string, unknown>)['isPinned']);
            if (!updated.isPinned && !hasA3('pinnedAt')) {
              updated.pinnedAt = undefined;
            }
          }
          if (hasA3('pinnedAt')) {
            const pinnedAt = this.toTimestamp((conversation as Record<string, unknown>)['pinnedAt']);
            updated.pinnedAt = pinnedAt > 0 ? pinnedAt : undefined;
            if (!hasA3('isPinned')) {
              updated.isPinned = !!updated.pinnedAt;
            }
          }
          // [Auto-sync] Merge thông tin khách hàng từ DO event — fanpageUserName, customerAvatar
          // Khi syncName hoàn tất, BE gửi DO event với tên thật → cập nhật ngay vào list
          if (hasA3('fanpageUserName')) updated.fanpageUserName = conversation.fanpageUserName!;
          // [FIX] Không ghi đè avatar nếu DO event gửi default avatar (token lỗi, BE fallback)
          if (hasA3('customerAvatar')) {
            const incomingAvatar = conversation.customerAvatar;
            if (incomingAvatar && !String(incomingAvatar).includes('default_avatar')) {
              updated.customerAvatar = incomingAvatar;
            }
          }

          // ── A3b: Fallback — nếu DO không gửi message/lastMessage
          // trong conversation object, lấy từ messages array để sidebar
          // vẫn cập nhật được preview text.
          if (messages.length) {
            const lastMsg = messages[messages.length - 1];
            if (lastMsg) {
              if (!hasA3('message') && !hasA3('lastMessage')) {
                const previewText = lastMsg.message || lastMsg.content || '';
                if (previewText) {
                  updated.message = previewText;
                  updated.lastMessage = previewText;
                } else if (lastMsg.attachments?.length) {
                  // Phân loại attachment type để hiển thị label
                  const attType = lastMsg.attachments[0]?.type || '';
                  switch (attType) {
                    case 'sticker':
                      updated.message = updated.lastMessage = LAST_MESSAGE_LABEL.STICKER;
                      break;
                    case 'image':
                    case 'photo':
                    case 'animated_image':
                      updated.message = updated.lastMessage = LAST_MESSAGE_LABEL.PHOTO;
                      break;
                    case 'video':
                    case 'video_inline':
                      updated.message = updated.lastMessage = LAST_MESSAGE_LABEL.VIDEO;
                      break;
                    case 'audio':
                    case 'voice':
                      updated.message = updated.lastMessage = LAST_MESSAGE_LABEL.AUDIO;
                      break;
                    default:
                      updated.message = updated.lastMessage = LAST_MESSAGE_LABEL.FILE;
                  }
                }
              }
              // [FIX] lastMessageId fallback độc lập — chạy cả khi có/không lastMessage
              if (lastMsg.id && !hasA3('lastMessageId')) {
                updated.lastMessageId = String(lastMsg.id);
              }
            }
          }

          // ── A4: Merge tags (FE13 lines 1484-1519) ──────────────────────
          // Xử lý tagId toggle: nếu tagId đã có → xóa, chưa có → thêm
          const convTagId = (conversation as any)?.tagId;
          if (convTagId) {
            const tagId = String(convTagId);
            const currentTags = Array.isArray(updated.tagIds) ? [...updated.tagIds] : [];
            const tIdx = currentTags.indexOf(tagId);
            if (tIdx >= 0) {
              currentTags.splice(tIdx, 1);
            } else {
              currentTags.push(tagId);
            }
            updated.tagIds = currentTags;
          }
          // [FIX] Merge tagIds trực tiếp từ conversation payload — xử lý trường hợp
          // ChatAreaComponent.onToggleTag() emitConversationChanges với {conversation: {id, tagIds}}
          // mà không dùng tagId/ops.addTagsId/ops.removeTagsId (FE21 path).
          // [FIX] Phải áp tagIds TRƯỚC ops.addTagsId/ops.removeTagsId: BE (tạo đơn tự gắn nhãn,
          // webhook POS...) gửi kèm tagIds cũ (chưa có tag vừa gắn) cùng ops.addTagsId — nếu áp
          // tagIds sau cùng sẽ ghi đè mất tag vừa gắn, phải F5 mới thấy.
          const convTagIds = (conversation as any)?.tagIds;
          if (Array.isArray(convTagIds)) {
            updated.tagIds = [...convTagIds];
          }
          // Xử lý ops.addTagsId: thêm các tag chưa có
          const addTagsId = (conversation as any)?.ops?.addTagsId;
          if (addTagsId) {
            const tagsToAdd: string[] = Array.isArray(addTagsId) ? addTagsId : [String(addTagsId)];
            const currentTags = Array.isArray(updated.tagIds) ? [...updated.tagIds] : [];
            for (const tag of tagsToAdd) {
              if (!currentTags.includes(tag)) currentTags.push(tag);
            }
            updated.tagIds = currentTags;
          }
          // Xử lý ops.removeTagsId: xóa các tag cần bỏ
          const removeTagsId = (conversation as any)?.ops?.removeTagsId;
          if (removeTagsId) {
            const tagsToRemove: string[] = Array.isArray(removeTagsId) ? removeTagsId : [String(removeTagsId)];
            if (Array.isArray(updated.tagIds)) {
              updated.tagIds = updated.tagIds.filter(t => !tagsToRemove.includes(t));
            }
          }

          // ── A5: Status + alertUnread (FE13 lines 1521-1534) ────────────
          if (conversation.status) {
            // Normalize — DO event có thể gửi status dạng string
            const rawStatus = Number(conversation.status);

            if (rawStatus !== updated.status) {
              if (rawStatus === CONVERSATION_STATUS.UNREAD) {
                this.updateBrowserTitle(1);
              } else {
                // Đã đọc — clear count
                updated.countUnread = 0;
              }
              updated.status = rawStatus;
            }

            // [FE21] Tăng countUnread cho mỗi tin nhắn mới từ customer khi hội thoại chưa đọc.
            // Không chỉ tăng khi status change — tăng cho mọi tin nhắn mới (FE13: đếm số tin từ customer).
            if (rawStatus === CONVERSATION_STATUS.UNREAD && messages.length) {
              const customerMsgs = messages.filter(m => String(m.senderId) !== String(conversation.fanpageId));
              if (customerMsgs.length > 0) {
                updated.countUnread = (updated.countUnread || 0) + customerMsgs.length;
              }
            }
          }

          // ── A6: assigneeIds + assignedGroupId (FE13 lines 1536-1541) ──
          if (conversation.assigneeIds) {
            updated.assigneeIds = conversation.assigneeIds;
          }
          if (Object.prototype.hasOwnProperty.call(conversation, 'assignedGroupId')) {
            updated.assignedGroupId = conversation.assignedGroupId;
          }

          // ── A7: topicItem (Telegram topic) merge (FE13 lines 1542-1566) ──
          const ops = (conversation as any)?.ops;
          if (ops?.topicItem) {
            const tp = ops.topicItem;
            const updatedAny = updated as any;
            const topics = Array.isArray(updatedAny.topics) ? [...updatedAny.topics] : [];
            // [FIX] Ép kiểu String() để tránh number vs string type mismatch từ DO event
            const tIdx = topics.findIndex((t: any) => String(t.id) === String(tp.id));
            if (tIdx >= 0) {
              if (tp.name !== undefined) topics[tIdx].name = tp.name;
              if (tp.lastMessage !== undefined) topics[tIdx].lastMessage = tp.lastMessage;
              if (tp.updatedTime !== undefined) topics[tIdx].updatedTime = tp.updatedTime;
              if (tp.status !== undefined) topics[tIdx].status = tp.status;
              // Sort topics
              topics.sort((a: any, b: any) => (b.updatedTime || 0) - (a.updatedTime || 0));
            } else {
              topics.push(tp);
              topics.sort((a: any, b: any) => (b.updatedTime || 0) - (a.updatedTime || 0));
            }
            updatedAny.topics = topics;
          }

          // ── A8: hasReply (FE13 lines 1568-1572) ────────────────────────
          if (Object.prototype.hasOwnProperty.call(conversation, 'hasReply')) {
            updated.hasReply = conversation.hasReply;
          } else if (messages.length && messages[0]?.senderId) {
            updated.hasReply = messages[0].senderId === conversation.fanpageId;
          }

          // ── A9: updatedTime (FE13 line 1573) ────────────────────────────
          // [FIX] Number() để xử lý BE gửi updatedTime dạng string "1781672435"
          if (conversation?.updatedTime) {
            updated.updatedTime = Number(conversation.updatedTime);
          } else if (messages.length) {
            // [FIX] Fallback updatedTime từ messages nếu conversation không gửi
            const lastMsg = messages[messages.length - 1];
            if (lastMsg?.createdAt) {
              const ts = Number(lastMsg.createdAt);
              updated.updatedTime = ts > 1e12 ? Math.floor(ts / 1000) : ts;
            } else if (lastMsg?.updatedTime) {
              updated.updatedTime = Number(lastMsg.updatedTime);
            }
          }

          // ── A10: hasPhone (FE13 line 1574) ─────────────────────────────
          if (Array.isArray(payload['phones'])) {
            updated.hasPhone = true;
          }

          // ── A11: playAlert khi không xem hội thoại này (FE13 lines 1767-1781) ──
          // FE13: nếu không đang xem hội thoại, messages có tin mới, status != READED
          // và không phải self-reassign → playAlert
          if (messages.length && updated.status !== CONVERSATION_STATUS.READED) {
            // Check reassign: nếu là reassign cho người khác thì không báo
            let shouldAlert = true;
            if (Object.prototype.hasOwnProperty.call(payload, 'reassign') && payload['reassign'] !== this.authService.getUid()) {
              shouldAlert = false;
            }
            // Check changeAssigneed + manager (FE13: changeAssigneed && checkManageBusiness)
            if (payload?.['changeAssigneed'] && this.splitService.checkManageBusiness(updated.fanpageId ?? '')) {
              shouldAlert = false;
            }
            if (shouldAlert) {
              this.soundService.playAlert(updated.type, updated.isMuted ?? false);
            }
          }

          // Cập nhật item trong danh sách
          currentItems[idx] = updated;
          shouldReorder = this.shouldReorderConversation(existing, updated, messages.length > 0);
          this.logger.debug('[DEBUG] ConvList DO update:', conversation.id, 'lastMessage:', updated.lastMessage, 'updatedTime:', updated.updatedTime, 'status:', updated.status);
          this._items$.next([...currentItems]);

        } else {
          this.logger.debug('[DEBUG-CONV] Entered BLOCK B');
          // ==================================================================
          // BLOCK B: Hội thoại mới (chưa có trong list)
          // ==================================================================
          if (!conversation?.type) return;

          const params = this.filterService.getCurrent();
          const filterState = this.extractFilterState(payload);

          // ── B1–B5: Bộ lọc danh sách (cid, loại, page, nhãn, chưa đọc, SĐT...) ──
          // Tự đánh giá, không gọi lại API cho từng event. Field định danh lấy từ payload; bộ lọc theo
          // trạng thái chỉ dùng filterState BE đọc lại sau khi ghi. Chưa đủ dữ liệu để biết hội thoại
          // có khớp không thì bỏ qua như FE13, không chèn đoán.
          let add = matchRealtimeConversationFilter(params, conversation, filterState) === REALTIME_FILTER_MATCH.MATCH;

          // ── B6: Check chia hội thoại (non-manager) ─────────────────────
          if (!this.limitPermission) {
            const hasSplit = this.splitService.hasSplitPage(conversation as any);
            if (hasSplit) {
              const hasAllow = this.splitService.hasAllowAssignee(conversation as any);
              if (hasAllow) {
                // Check lastAssignedAt trong vòng 5p → đánh dấu UNREAD
                const lastAssignedAt = (conversation as any)?.lastAssignedAt;
                if (lastAssignedAt && Number(lastAssignedAt) > 0) {
                  const nowUnix = Math.floor(Date.now() / 1000);
                  if ((nowUnix - Number(lastAssignedAt)) < 300) {
                    conversation.status = CONVERSATION_STATUS.UNREAD;
                  }
                }
              } else {
                add = false;
              }
            }
            const isAddNew = this.splitService.isAddNewConversationSocket(conversation as any);
            if (!isAddNew) add = false;
          }

          // ── B7: Check manager filter (hasAddRecordFilter) ────────────────
          // Có filterState thì dùng người được chia sau khi ghi, không thì giữ cách FE13 đọc từ payload.
          if (add) {
            if (params['divisionType'] || params.assigneeIds?.length || params.assignedGroupId) {
              const assignee = filterState ?? conversation;
              add = this.splitService.hasAddRecordFilter({
                assigneeIds: assignee.assigneeIds ?? undefined,
                assignedGroupId: assignee.assignedGroupId ?? undefined,
              }, params as any);
            }
          }

          // ── B8: Nếu không add được → set status cho conv (FE13 lines 1864-1869) ──
          if (!add) {
            const convAny = conversation as any;
            if (messages.length && messages[0]?.senderId === conversation.fanpageId) {
              convAny.status = CONVERSATION_STATUS.READED;
            } else {
              convAny.status = CONVERSATION_STATUS.UNREAD;
            }
          }

          // ── B9: Thêm vào list + playAlert ────────────────────────────────
          if (add) {
            // Play sound
            this.soundService.playAlert(conversation.type ?? CONVERSATION_TYPE.INBOX, conversation.isMuted ?? false);

            // Alert unread title
            if ((conversation as any).status !== CONVERSATION_STATUS.READED) {
              this.updateBrowserTitle(1);
              (conversation as any).countUnread = ((conversation as any).countUnread || 0) + 1;
            }

            // Add to list — hiển thị theo trạng thái sau khi ghi (nhãn...) thay vì phần payload event ghi
            const inserted = this.applyFilterState(conversation, filterState) as IConversation;
            this._items$.next([inserted, ...currentItems]);

            // Đồng bộ ngay khi webhook tạo hội thoại có tên/avatar placeholder.
            // Có trường hợp BE đã có tên nhưng customerAvatar vẫn là
            // default_avatar.png, nên không được chỉ kiểm tra tên.
            this.customerSyncSvc.syncNewConversation(inserted);
          }
        }

        // ==================================================================
        // BLOCK C: Xử lý chung sau merge
        // ==================================================================

        // ── C1: postItem.threadId (FE13 lines 1783-1790) ─────────────────
        // [FIX] Ép kiểu String() để tránh number vs string type mismatch từ DO event
        if (payload?.['postItem'] && String((payload['postItem'] as any)?.conversationId) === String(conversation.id)) {
          const postItem = payload['postItem'] as any;
          // NOTE: postItem.threadId update thuộc về ChatCoreService (message-level)
          // emit event để ChatAreaComponent xử lý nếu cần
          this.interactionService.emitEvent('postItem', postItem);
        }

        // ── C2: sort + recount ─────────────────────────────────────────────
        if (shouldReorder) this.sortItems();
        this.recountUnread();

        // ── C3: scheduleAutoUnmute (FE13 lines 1906-1911) ────────────────
        const updatedItem = idx >= 0 ? currentItems[idx] : null;
        if (updatedItem?.isMuted) {
          if ((updatedItem as any)?.muteUntil != null) {
            (updatedItem as any).muteUntilTs = Number((updatedItem as any).muteUntil);
          }
          // MuteConversationService đã có expired$ handle ở bindMuteExpiry()
        }

        // ── C4: Guest upgrade (FE13 lines 1914-1923) ─────────────────────
        if (Object.prototype.hasOwnProperty.call(payload, 'upgradeFromGuest')) {
          const guestConvId = payload['upgradeFromGuest'] as string;
          this.removeConversation(guestConvId);
        }
      } catch (err) {
        this.logger.error('[ConversationList] DO handler error:', err);
      }
    });

    // ─── Tag changed (page event) ────────────────────────────────────────
    // Cài đặt nhãn bắn {payload: {type: 'tag', ...}} (FE13 handleTagChanged) → cập nhật danh sách nhãn;
    // {payload: {type: TAG_SETTING_PAYLOAD_TYPE, pageId, tags}} → cài đặt hiển thị gợi ý nhãn của page
    this.realtime.tagChanged$.subscribe(evt => {
      const payload = evt.data as
        { conversationId?: string; tagIds?: string[]; payload?: {type?: string; pageId?: string; tags?: unknown} } | undefined;
      if (payload?.payload?.type === TAG_SETTING_PAYLOAD_TYPE) {
        this.pageSettings.patch(String(payload.payload.pageId || ''), {tags: payload.payload.tags});
      } else if (payload?.payload) {
        this.tagService.handleTagChanged(payload.payload);
      }
      if (payload?.conversationId) {
        this.updateConversationInList(payload.conversationId, {
          tagIds: Array.isArray(payload.tagIds) ? payload.tagIds : []
        });
      }
      this.recountUnread();
    });

    // ─── Business tag changed (FE13: handleTagChanged cho business event) ─
    this.realtime.businessTagChanged$.subscribe(evt => {
      const payload = evt.data as
        { conversationId?: string; tagIds?: string[]; payload?: unknown } | undefined;
      if (payload?.payload) {
        this.tagService.handleTagChanged(payload.payload);
      }
      if (payload?.conversationId) {
        this.updateConversationInList(payload.conversationId, {
          tagIds: Array.isArray(payload.tagIds) ? payload.tagIds : []
        });
      }
    });

    // ─── Quick message changed (FE13: handleQuickMessageChanged) ─
    // Xoá cache trong QuickMessageService để lần mở panel sau tải lại từ API
    // Dùng (evt.data as any).payload để match FE13 — DO event có wrapper .payload
    this.realtime.businessQuickMessageChanged$.subscribe(evt => {
      const data = (evt.data as any)?.payload ?? evt.data;
      this.quickMsgSvc.handleQuickMessageChanged(data);
    });
    this.realtime.quickMessageChanged$.subscribe(evt => {
      const data = (evt.data as any)?.payload ?? evt.data;
      this.quickMsgSvc.handleQuickMessageChanged(data);
    });

    // ─── Typing ──────────────────────────────────────────────────────────
    this.realtime.typing$.subscribe(evt => {
      const payload = evt.data as { conversationId?: string } | undefined;
      if (payload?.conversationId) {
        this.updateConversationInList(payload.conversationId, {is_typing: 1});
      }
    });

    // ─── Stop typing ─────────────────────────────────────────────────────
    this.realtime.stopTyping$.subscribe(evt => {
      const payload = evt.data as { conversationId?: string } | undefined;
      if (payload?.conversationId) {
        this.updateConversationInList(payload.conversationId, {is_typing: 0});
      }
    });

    // Zalo cá nhân gửi `typingEvent` bên trong conversation:changes. FE13 giữ
    // trạng thái tối đa 6 giây, vì payload này không có event dừng gõ riêng.
    this.realtime.zaloPersonalTyping$.pipe(takeUntil(this._destroy$)).subscribe(evt => {
      const payload = evt.data;
      const conversationId = String(payload.conversationId ?? '');
      if (!conversationId) return;

      this.updateConversationInList(conversationId, {is_typing: 1});
      const previousTimer = this.zaloPersonalTypingTimers.get(conversationId);
      if (previousTimer) clearTimeout(previousTimer);

      const ttl = this.getZaloPersonalTypingTtl(payload.time);
      if (ttl <= 0) {
        this.updateConversationInList(conversationId, {is_typing: 0});
        return;
      }
      this.zaloPersonalTypingTimers.set(conversationId, setTimeout(() => {
        this.zaloPersonalTypingTimers.delete(conversationId);
        this.updateConversationInList(conversationId, {is_typing: 0});
      }, ttl));
    });

    // ─── Recommend product (display badge) ───────────────────────────────
    this.realtime.recommendProduct$.subscribe(evt => {
      const payload = evt.data as { conversationId?: string; products?: unknown[] } | undefined;
      if (!payload?.conversationId) return;
      const ids = Array.isArray(payload.products)
        ? payload.products
          .map(p => (p && typeof p === 'object' ? (p as Record<string, unknown>)['id'] : p))
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
        : [];
      this.updateConversationInList(payload.conversationId, {
        recommendedProductIds: ids,
      });
    });
  }

  /** `filterState`: trạng thái lọc BE đọc lại sau khi ghi (IConversationFilterState). Event không có → null. */
  private extractFilterState(payload: Record<string, unknown>): IConversationFilterState | null {
    const state = payload['filterState'];
    return state && typeof state === 'object' && !Array.isArray(state) ? state as IConversationFilterState : null;
  }

  /**
   * Payload webhook không gửi nhãn và luôn gửi hasPhone=false; chèn nguyên payload thì hội thoại thiếu nhãn
   * tới khi F5. Giữ status của payload — B6 có thể đã đánh dấu chưa đọc cho hội thoại vừa được chia.
   */
  private applyFilterState(conversation: Partial<IConversation>, state: IConversationFilterState | null): Partial<IConversation> {
    if (!state) return conversation;
    const next: Partial<IConversation> = {...conversation, tagIds: Array.isArray(state.tagIds) ? state.tagIds.map(String) : []};
    if (state.hasPhone != null) next.hasPhone = state.hasPhone;
    if (state.hasReply != null) next.hasReply = state.hasReply;
    if (state.hasDraftOrder != null) next.hasDraftOrder = state.hasDraftOrder;
    if (state.chatbotStatus != null) next.chatbotStatus = state.chatbotStatus;
    if (Array.isArray(state.assigneeIds)) next.assigneeIds = state.assigneeIds;
    if (state.assignedGroupId != null) next.assignedGroupId = state.assignedGroupId;
    return next;
  }

  private getZaloPersonalTypingTtl(time: number | undefined): number {
    const eventTime = Number(time);
    const maxTtl = 6000;
    if (!Number.isFinite(eventTime) || eventTime <= 0) return maxTtl;

    const eventTimeMs = eventTime < 1_000_000_000_000 ? eventTime * 1000 : eventTime;
    return Math.min(maxTtl, Math.max(0, eventTimeMs + maxTtl - Date.now()));
  }

  // ===========================================================================
  // SECTION 1: LẤY DỮ LIỆU TỪ API
  // ===========================================================================

  /**
   * Lấy mapping businessId → pageIds của toàn bộ các page đã đăng nhập để gửi lên server.
   * Mục đích: Màn hình hội thoại không lọc theo 1 doanh nghiệp mà tải hội thoại của tất cả các page đã login.
   * Server dùng map này để kiểm tra cấu hình chia hội thoại theo từng doanh nghiệp tương ứng.
   *
   * @returns Object dạng { [businessId: string]: string[], noBusiness?: string[] }
   *          Ví dụ: { "15113": ["1036089299598472"], "34369": ["167890463074771"], "noBusiness": ["..."] }
   */
  private getDataBusiness(): Record<string, string[]> {
    // 1. Lấy toàn bộ các page đã login (của tất cả doanh nghiệp, không bị lọc theo activeBusiness ở Header)
    const pages = this.authService.getAllPages();
    const params: Record<string, string[]> = {};
    if (!pages || !Array.isArray(pages) || pages.length === 0) {
      return params;
    }

    // 2. Nhóm pageId theo từng businessId
    pages.forEach((p: any) => {
      if (!p || p.id === undefined || p.id === null) {
        return;
      }

      // Xác định key: Nếu có businessId hợp lệ thì dùng String(businessId), ngược lại gom vào 'noBusiness'
      const bId = p.businessId;
      const key = (bId !== undefined && bId !== null && bId !== '' && bId !== 0 && bId !== '0')
        ? String(bId)
        : 'noBusiness';

      if (!params[key]) {
        params[key] = [];
      }

      const pageId = String(p.id);
      if (!params[key].includes(pageId)) {
        params[key].push(pageId);
      }
    });

    return params;
  }

  /**
   * Tải danh sách hội thoại từ server.
   * Response format: IResponseApi<IResDataElasticDefault<IConversation[]>>
   * → res.data.result = mảng hội thoại
   * → res.data.next = cursor phân trang
   *
   * @param params Bộ lọc (Filter) gửi lên server (nhãn, kênh, trạng thái...)
   * @param isLoadMore Nếu true = phân trang (cộng dồn), false = thay thế hoàn toàn
   */
  public fetchList(params: IConversationFilterParam, isLoadMore = false): void {
    const pinnedScanToken = isLoadMore
      ? this.pinnedConversationScanToken
      : ++this.pinnedConversationScanToken;
    // Bước 1: Chống duplicate request — nhưng cho phép search/filter mới thay thế request cũ
    if (this._isLoading$.getValue()) {
      if (!isLoadMore) {
        // FE21: filter/search mới (không phải load-more) → ưu tiên request mới,
        // reset loading để tránh nuốt mất request clearSearch / search lại
        this._isLoading$.next(false);
      } else {
        return;
      }
    }

    // Bước 2: Load more mà hết cursor → dừng
    if (isLoadMore && !this._nextPageCursor) return;

    this.lastFetchedParamsSignature = this.paramsSignature(params);

    // Bước 3: Bật loading
    this._isLoading$.next(true);

    // Bước 3.1: Clear list ngay khi fetch (trừ load-more) để loading state hiển thị
    // FE13 behavior: khi đổi filter/bỏ lọc → clear danh sách cũ, hiện spinner
    if (!isLoadMore) {
      this._items$.next([]);
      this._filteredResultCount$.next(null);

      // Clear search results panel ngay khi có search mới — tránh show kết quả cũ
      // trong lúc đang chờ API search mới trả về (fix: không thấy loading)
      if (this.searchService.searchResultsSnapshot !== null) {
        this.searchService.clearSearch();
      }
    }

    // Bước 4: Chuẩn bị params — clean + đính kèm dataBussiness
    const requestParams = this.buildRequestParams(params, isLoadMore);
    const requestSeq = ++this.listRequestSeq;

    // Bước 5: Gọi API chính
    this.api.getConversation(requestParams).subscribe({
      next: (res: IResponseApi<any>) => {
        // Đã có request mới hơn (đổi lọc/sắp xếp nhanh): bỏ kết quả cũ, request mới tự tắt loading.
        if (requestSeq !== this.listRequestSeq) return;

        // Bước 6: Kiểm tra response code
        if (res?.code !== 1 || !res?.data) {
          this.logger.warn('[ConversationList] API trả về không thành công — messages:', res?.messages, '| errorCode:', res?.errorCode, '| statusCode:', res?.statusCode);
          this._isLoading$.next(false);
          return;
        }

        // Bước 7: Parse response
        // BE trả res.data.scroller.items (elasticsearch scroll format) hoặc res.data.items
        // Xem thêm getPostsByPage() trong conversation-api.service.ts để biết pattern tương tự.
        const resData = (res.data.scroller ?? res.data) as IConversationListResponse;
        const rawItems: IConversation[] = (resData.items || resData.result || [])
          .filter(item => !params.cid || String(item.id) === String(params.cid));
        if (!isLoadMore) {
          // CID xác định một hội thoại duy nhất, kể cả khi BE không trả totalItems.
          this._filteredResultCount$.next(params.cid ? rawItems.length : this.extractFilteredResultCount(resData));
        }
        const items: IConversation[] = rawItems.map(item => this.normalizePinnedConversation({
          ...item,
          status: Number(item.status),
        }));

        // Lưu cursor phân trang — ưu tiên từ scroller.next, fallback resData.next
        this._nextPageCursor = resData?.next ?? resData.next ?? null;

        // [§3.B.9] Trích xuất search results — FE13 parity: luôn hiển thị panel khi có customerName/keyword
        const isSearch = !!(params.customerName || params.keyword);
        if (isSearch) {
          const sd = resData.searchedData;
          // BE có thể trả searchedData (messages + conversationIds) hoặc chỉ scroller.items (conversations đã lọc)
          const searchMessages = sd?.allMessages ?? [];

          // API items are the loaded inbox page. FE13 applies its own name/alias/phone matcher below.
          const totalMessages = sd?.totalMessages ?? searchMessages.length;

          /**
           * FE13 keeps conversation and message matches separate. A message's
           * conversationId is only its owner; it is not a conversation-name match.
           */
          const normalizedKeyword = this.searchService.removeAccents(
            String(params.customerName ?? params.keyword ?? ''),
          );
          const matchedCustomerIds = new Set(
            (sd?.fanpageUserIds ?? []).map(id => String(id)),
          );
          const searchConversations = params.typeSearch === CONVERSATION_SEARCH_TYPE.NHANH_POS_ORDER
            // Mã đơn không thuộc các field tên/alias của hội thoại. BE đã lọc theo mã đơn,
            // nên dùng trực tiếp danh sách trả về thay vì áp dụng matcher tìm khách cũ.
            ? items
            : items.filter(conversation => {
              const alias = this.searchService.removeAccents(conversation.pageUserAlias ?? '');
              const name = this.searchService.removeAccents(conversation.fanpageUserName ?? '');
              return alias.includes(normalizedKeyword)
                || name.includes(normalizedKeyword)
                || matchedCustomerIds.has(String(conversation.fanpageUserId));
            });
          const conversationsById = new Map(items.map(conversation => [String(conversation.id), conversation]));
          const normalizedSearchMessages = searchMessages.map(message => {
            const conversationId = message.conversationId || message.conversation?.id;
            const conversation = conversationId
              ? conversationsById.get(String(conversationId))
              : undefined;
            return conversation && !message.conversation
              ? {...message, conversation}
              : message;
          });

          // [FE13 parity] Group messages by conversation ID → gán filteredSearchedData cho từng conversation
          // để hiển thị tin nhắn match inline ngay dưới item hội thoại (search-group)
          const messagesByConvId = new Map<string, ISearchMessage[]>();
          for (const msg of normalizedSearchMessages) {
            // FE13: msg.conversationId trực tiếp hoặc msg.conversation?.id (buildSearchMessageConv dùng cả 2)
            const convId = msg.conversationId || msg.conversation?.id;
            if (convId) {
              if (!messagesByConvId.has(convId)) {
                messagesByConvId.set(convId, []);
              }
              messagesByConvId.get(convId)!.push(msg);
            }
          }
          for (const conv of searchConversations) {
            const msgs = messagesByConvId.get(conv.id);
            if (msgs?.length) {
              conv.filteredSearchedData = {
                messages: msgs,
                totalMessages: msgs.length,
              };
            }
          }

          this.searchService.setSearchResults({
            conversations: searchConversations,
            messages: normalizedSearchMessages,
            totalMessages,
            keyword: params.customerName ?? params.keyword,
          });
        } else {
          // Không phải search => clear kết quả cũ (nếu có), reset tab
          if (this.searchService.searchResultsSnapshot !== null) {
            this.searchService.clearSearch();
          }
        }

        // Bước 9: Xử lý kết quả
        if (isLoadMore) {
          this.mergeAndAppend(items);
        } else {
          this.setInitial(items);
        }

        // Bước 9.1: Populate split data (pageManageIds, leaderGroupIds...) từ response
        // FE13: splitConversationService.setDataFromConversation(conversations)
        this.splitService.setDataFromConversation(resData);

        // Bước 9.2: Auto-sync tên/avatar placeholder từ platform.
        // Gọi lần lượt bằng await, không chạy đồng thời để tránh áp lực BE; tải mới thì dừng chuỗi của lượt trước.
        if (!isSearch) {
          void this.customerSyncSvc.syncDefaultNames(items, !isLoadMore);
        }

        // FE13 scans the following server page once on the first load. The API
        // orders unread first, so older pinned conversations otherwise appear
        // only after the user scrolls to the bottom of the unread list.
        if (!isLoadMore && !isSearch && this.shouldLoadPinnedConversations(params) && this._nextPageCursor) {
          void this.mergePinnedConversationsFromNextPage(
            requestParams,
            this._nextPageCursor,
            pinnedScanToken,
          );
        }

        // Bước 10: Đếm hội thoại chưa đọc → cập nhật title tab
        this.recountUnread();

        this._isLoading$.next(false);
      },
      error: (err) => {
        if (requestSeq !== this.listRequestSeq) return;
        this.logger.error('[ConversationList] Lỗi tải danh sách:', err);
        if (!isLoadMore) {
          this._filteredResultCount$.next(null);
        }
        this._isLoading$.next(false);
      }
    });
  }

  /**
   * Xây dựng request params từ filter đầu vào.
   * - Xóa các key null/undefined
   * - Đính kèm dataBussiness
   * - Xử lý cursor phân trang
   * - Xử lý searchContent lưu/đọc localStorage
   */
  private buildRequestParams(params: IConversationFilterParam, isLoadMore: boolean): IConversationFilterParam {
    // Sao chép params để không mutate gốc
    const clean: IConversationFilterParam = {...params};

    // Xử lý searchContent: lưu vào localStorage nếu có, đọc lại nếu không có
    if (Object.prototype.hasOwnProperty.call(clean, 'searchContent')) {
      if (!clean.searchContent) {
        delete clean.searchContent;
        if (typeof localStorage !== 'undefined') localStorage.removeItem('searchCustomer');
      } else {
        if (typeof localStorage !== 'undefined') localStorage.setItem('searchCustomer', String(clean.searchContent));
      }
    } else {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('searchCustomer');
        if (saved) clean.searchContent = saved;
      }
    }

    // Xóa customerName rỗng
    if (!clean.customerName) {
      delete clean.customerName;
      delete clean.typeSearch;
    }

    // Xóa key null/undefined để clean request
    Object.keys(clean).forEach(key => {
      if (clean[key] === null || clean[key] === undefined) {
        delete clean[key];
      }
    });

    // Đính kèm dataBussiness (mapping page theo business)
    clean.dataBussiness = JSON.stringify(this.getDataBusiness());

    // Đính kèm sort mode nếu chưa được set từ bên ngoài
    // FE13: gửi sortActivities lên server để sắp xếp server-side
    if (clean.sort === undefined || clean.sort === null) {
      clean.sort = this._sortMode;
    }

    // FE13 parity: limit số bản ghi / trang (từ user setting display)
    clean['limit'] = this._limit;

    // V2 chỉ cần tính tổng ở request đầu. Các trang cursor không gửi field này
    // để tránh BE thực hiện lại truy vấn totalItems khi người dùng cuộn danh sách.
    if (isLoadMore) {
      delete clean.withTotalItems;
    } else {
      clean.withTotalItems = true;
    }

    // Đính kèm cursor phân trang nếu là load more
    if (isLoadMore && this._nextPageCursor) {
      clean.after = this._nextPageCursor as any;
    }

    return clean;
  }

  /**
   * Hàm tiện ích khi cuộn xuống cuối danh sách.
   * @param currentParams Tham số lọc hiện thời
   */
  public loadNextPage(currentParams: IConversationFilterParam): void {
    if (!this._nextPageCursor || this._isLoading$.getValue()) return;
    this.fetchList(currentParams, true);
  }

  /** [§3.B.9] Xóa kết quả tìm kiếm, reset tab về 'all' */
  public clearSearch(): void {
    this.searchService.clearSearch();
  }

  // ===========================================================================
  // SECTION 2: QUẢN LÝ STATE DANH SÁCH
  // ===========================================================================

  /**
   * Thiết lập danh sách ban đầu (khi vào trang / đổi filter)
   */
  public setInitial(items: IConversation[]): void {
    this._items$.next(items || []);
    this.sortItems();
  }

  /**
   * Merge chống trùng ID rồi append vào cuối danh sách (infinite scroll)
   */
  private mergeAndAppend(newItems: IConversation[]): void {
    const current = this._items$.getValue();
    // Lọc bỏ item có ID đã tồn tại trong danh sách hiện tại
    // [FIX] Ép kiểu String() để tránh number vs string type mismatch từ DO event
    const unique = (newItems || []).filter(item => !current.some(c => String(c.id) === String(item.id)));
    this._items$.next([...current, ...unique]);
    this.sortItems();
  }

  /**
   * Merge 2 mảng hội thoại, đảm bảo không trùng ID.
   * Hàm public để các service khác có thể sử dụng (VD: merge partner conversations).
   * @param existing Mảng hiện tại
   * @param incoming Mảng mới cần merge
   * @returns Mảng đã merge, unique theo id
   */
  public mergeItems(existing: IConversation[], incoming: IConversation[]): IConversation[] {
    if (!incoming?.length) return existing || [];
    if (!existing?.length) return incoming;
    // [FIX] Ép kiểu String() để tránh number vs string type mismatch từ DO event
    const existingIds = new Set(existing.map(c => String(c.id)));
    const unique = incoming.filter(item => !existingIds.has(String(item.id)));
    return [...existing, ...unique];
  }

  /**
   * Cập nhật thông tin một hội thoại trong danh sách (Socket / Optimistic Update)
   */
  public updateConversationInList(convId: string, data: Partial<IConversation>): void {
    const current = this._items$.getValue();
    // [FIX] Ép kiểu String() để tránh number vs string type mismatch từ DO event
    const idx = current.findIndex(c => String(c.id) === String(convId));
    if (idx >= 0) {
      // Merge dữ liệu cũ + mới
      const previous = current[idx];
      current[idx] = {...previous, ...data};
      this._items$.next([...current]);
      if (this.shouldReorderConversation(previous, current[idx])) this.sortItems();
    }

    // Search results are held separately from the sidebar list. Sync the
    // snapshot too so an optimistic state change is visible in all search tabs.
    const searchResults = this.searchService.searchResultsSnapshot;
    if (!searchResults) return;

    let hasChanged = false;
    const conversations = searchResults.conversations.map(conversation => {
      if (String(conversation.id) !== String(convId)) return conversation;
      hasChanged = true;
      return {...conversation, ...data};
    });
    const messages = searchResults.messages.map(message => {
      const messageConversationId = message.conversationId || message.conversation?.id;
      if (String(messageConversationId) !== String(convId)) return message;
      hasChanged = true;
      return {
        ...message,
        conversation: {...message.conversation, ...data} as IConversation,
      };
    });

    if (hasChanged) {
      this.searchService.setSearchResults({...searchResults, conversations, messages});
    }
  }

  /**
   * Extension định danh customer là `<fanpageId>_<fanpageUserId>`, trong khi
   * conversation id của comment/inbox có thể có thêm segment. Patch mọi item
   * cùng customer identity để avatar không chỉ đổi sau khi F5.
   */
  public updateConversationsByCustomerId(customerId: string, data: Partial<IConversation>): void {
    if (!customerId) return;

    const matchesCustomer = (conversation: Partial<IConversation>): boolean =>
      String(conversation.id) === String(customerId)
      || `${conversation.fanpageId}_${conversation.fanpageUserId}` === String(customerId);

    const current = this._items$.getValue();
    const updatedItems = current.map(conversation =>
      matchesCustomer(conversation) ? {...conversation, ...data} : conversation,
    );
    if (updatedItems.some((conversation, index) => conversation !== current[index])) {
      this._items$.next(updatedItems);
      if (updatedItems.some((conversation, index) => this.shouldReorderConversation(current[index], conversation))) {
        this.sortItems();
      }
    }

    const searchResults = this.searchService.searchResultsSnapshot;
    if (!searchResults) return;

    let hasChanged = false;
    const conversations = searchResults.conversations.map(conversation => {
      if (!matchesCustomer(conversation)) return conversation;
      hasChanged = true;
      return {...conversation, ...data};
    });
    const messages = searchResults.messages.map(message => {
      const conversation = message.conversation;
      if (!conversation || !matchesCustomer(conversation)) return message;
      hasChanged = true;
      return {...message, conversation: {...conversation, ...data} as IConversation};
    });
    if (hasChanged) {
      this.searchService.setSearchResults({...searchResults, conversations, messages});
    }
  }

  /**
   * Xử lý cập nhật / xóa hội thoại khỏi danh sách sau khi phân công nhân viên hoặc nhóm.
   * Được gọi từ các component phân công (ChatAreaComponent, CustomerRightPanelComponent).
   *
   * @param convId ID hội thoại
   * @param pageId ID page
   * @param assigneeIds Danh sách nhân viên sau khi gán/gỡ
   * @param assignedGroupId ID nhóm sau khi gán/gỡ
   * @returns boolean: true nếu hội thoại bị xóa khỏi danh sách, false nếu chỉ cập nhật
   */
  public handleAfterAssigneeChanged(
    convId: string,
    pageId: string | undefined,
    assigneeIds: string[],
    assignedGroupId?: string,
  ): boolean {
    if (!convId) return false;
    const currentItems = this._items$.getValue();
    const existing = currentItems.find(c => String(c.id) === String(convId));
    const targetPageId = pageId || existing?.fanpageId;

    const convToCheck = {
      id: convId,
      fanpageId: targetPageId,
      assigneeIds: assigneeIds,
      assignedGroupId: assignedGroupId !== undefined ? assignedGroupId : existing?.assignedGroupId,
    };

    // 1. Nếu là nhân viên thường (không có quyền quản lý): kiểm tra quyền xem theo phân công
    if (!this.limitPermission) {
      const hasSplit = this.splitService.hasSplitPage(convToCheck as any);
      if (hasSplit) {
        const hasAllow = this.splitService.hasAllowAssignee(convToCheck as any);
        if (!hasAllow) {
          // Xóa khỏi danh sách vì nhân viên không còn được phân công
          this.removeConversation(convId);
          this.interactionService.emitEvent('conversationBlockedOrRemoved', {
            conversationId: String(convId),
            reason: 'unassigned',
          });
          return true;
        }
      }
    }

    // 2. Nếu đang áp dụng bộ lọc (quản lý lọc theo divisionType, nhân viên, nhóm)
    const filterParams = this.filterService.getCurrent();
    if (filterParams['divisionType'] || filterParams.assigneeIds?.length || filterParams.assignedGroupId) {
      const shouldRemove = this.splitService.hasRemoveRecordFilter(
        convToCheck as any,
        filterParams as any,
      );
      if (shouldRemove) {
        // Xóa khỏi danh sách vì không còn thỏa mãn bộ lọc hiện tại
        this.removeConversation(convId);
        this.interactionService.emitEvent('conversationBlockedOrRemoved', {
          conversationId: String(convId),
          reason: 'filtered_out',
        });
        return true;
      }
    }

    // 3. Nếu vẫn thỏa mãn: cập nhật dữ liệu hội thoại trong danh sách
    this.updateConversationInList(convId, {
      assigneeIds,
      assignedGroupId: assignedGroupId ?? '',
    });
    return false;
  }

  /**
   * Xóa hội thoại khỏi danh sách
   */
  public removeConversation(convId: string): void {
    const current = this._items$.getValue();
    // [FIX] Ép kiểu String() để tránh number vs string type mismatch từ DO event
    this._items$.next(current.filter(c => String(c.id) !== String(convId)));
  }

  /**
   * Upsert hội thoại: đã tồn tại → merge, chưa tồn tại → thêm đầu.
   * Dùng cho socket khi có hội thoại mới.
   */
  public upsertConversation(incoming: IConversation): void {
    const cid = this.filterService.getCurrent().cid;
    if (cid && String(cid) !== String(incoming.id)) return;
    const current = this._items$.getValue();
    // [FIX] Ép kiểu String() để tránh number vs string type mismatch từ DO event
    const idx = current.findIndex(c => String(c.id) === String(incoming.id));

    if (idx >= 0) {
      // Đã tồn tại → merge
      current[idx] = {...current[idx], ...incoming};
      this._items$.next([...current]);
    } else {
      // Chưa tồn tại → thêm vào đầu
      this._items$.next([incoming, ...current]);
    }
    this.sortItems();
  }

  /** Trả về snapshot danh sách hiện tại */
  public getCurrentItems(): IConversation[] {
    return this._items$.getValue();
  }

  /** Kiểm tra danh sách hiện tại đã được tải cho đúng bộ filter này chưa. */
  public hasFetchedListFor(params: IConversationFilterParam): boolean {
    return this.lastFetchedParamsSignature === this.paramsSignature(params);
  }

  /** Reset toàn bộ state (khi logout / đổi business) */
  public resetState(): void {
    this.listRequestSeq++;
    this._items$.next([]);
    this._filteredResultCount$.next(null);
    this._nextPageCursor = null;
    this._nextAffCursor = null;
    this.lastFetchedParamsSignature = null;
    this._isLoading$.next(false);
    this._alertUnread$.next(0);
  }

  /** Không fallback theo độ dài trang vì cursor chưa chắc đã tải hết kết quả. */
  private extractFilteredResultCount(data: IConversationListResponse): number | null {
    const count = Number(data.totalItems);
    return Number.isFinite(count) && count >= 0 ? count : null;
  }

  private paramsSignature(params: IConversationFilterParam): string {
    // Gồm cả sort thực gửi đi: đổi kiểu sắp xếp thì danh sách đã tải không còn đúng bộ lọc.
    const withSort = {...params, sort: params.sort ?? this._sortMode};
    return JSON.stringify(
      Object.entries(withSort)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]),
    );
  }

  // ===========================================================================
  // SECTION 3: SẮP XẾP 3 KIỂU (UNREAD / TIME / NO_REPLY)
  // ===========================================================================

  /**
   * Cập nhật kiểu sort. Gọi khi user thay đổi setting "sortActivities".
   * @param mode ESortMode
   */
  public setSortMode(mode: ESortMode): void {
    this._sortMode = mode;
    this.sortItems();
  }

  /**
   * Áp dụng sort mode từ user settings (gọi sau khi ConversationSettingsService loaded).
   * @param mode ESortMode từ user settings
   * @returns true nếu sort mode thay đổi (caller nên trigger re-fetch)
   */
  public applySortModeFromSettings(mode: ESortMode): boolean {
    if (this._sortMode !== mode) {
      this._sortMode = mode;
      this.sortItems();
      return true;
    }
    return false;
  }

  private seedSortModeFromAuth(): void {
    const saved = Number(this.authService.getSetting('sortActivities'));
    if (saved === ESortMode.UNREAD_FIRST || saved === ESortMode.BY_TIME || saved === ESortMode.NO_REPLY_FIRST) {
      this._sortMode = saved;
    }
  }

  /**
   * Áp dụng display limit từ user settings (gọi sau khi UserSettingsService loaded).
   * FE13: db.userSetting.display → số item mỗi trang.
   */
  public applyLimitFromSettings(): void {
    const display = this.userSettings.displayLimit();
    if (display > 0) {
      this._limit = display;
    }
  }

  /**
   * So sánh ưu tiên ghim giữa 2 hội thoại.
   * Hội thoại ghim luôn lên trước hội thoại không ghim.
   * Nếu cả 2 đều ghim → sắp theo pinnedAt (ghim sau lên trước).
   * @returns -1 nếu a lên trước, 1 nếu b lên trước, 0 nếu bằng nhau
   */
  private comparePinned(a: IConversation, b: IConversation): number {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    // Cả 2 đều ghim → sắp theo pinnedAt mới nhất lên trên
    if (a.isPinned && b.isPinned) {
      const activityA = Math.max(this.toTimestamp(a.updatedTime), this.toTimestamp(a.pinnedAt));
      const activityB = Math.max(this.toTimestamp(b.updatedTime), this.toTimestamp(b.pinnedAt));
      if (activityA !== activityB) return activityB - activityA;
      return this.toTimestamp(b.pinnedAt) - this.toTimestamp(a.pinnedAt);
    }
    return 0;
  }

  /** FE13 treats a non-empty pinnedAt as the canonical pinned state. */
  private normalizePinnedConversation(conversation: IConversation): IConversation {
    const pinnedAt = conversation.pinnedAt || undefined;
    return {
      ...conversation,
      pinnedAt,
      isPinned: conversation.isPinned ?? !!pinnedAt,
    };
  }

  private shouldLoadPinnedConversations(params: IConversationFilterParam): boolean {
    const ignoredKeys = new Set(['filterPageIds', 'filterAffPageIds', 'channelIds', 'type', 'sort']);
    return !Object.entries(params).some(([key, value]) => {
      if (ignoredKeys.has(key)) return false;
      return value !== null && value !== undefined && value !== '';
    });
  }

  private async mergePinnedConversationsFromNextPage(
    requestParams: IConversationFilterParam,
    nextCursor: any,
    token: number,
  ): Promise<void> {
    try {
      // Đây cũng là request cursor nội bộ, không yêu cầu BE tính lại totalItems.
      const {withTotalItems: _withTotalItems, ...nextRequestParams} = requestParams;
      const response = await firstValueFrom(this.api.getConversation({
        ...nextRequestParams,
        after: nextCursor,
      }));
      if (token !== this.pinnedConversationScanToken || response?.code !== 1 || !response.data) return;

      const data = (response.data.scroller ?? response.data) as IConversationListResponse;
      const pinnedItems = (data.items || data.result || [])
        .map(item => this.normalizePinnedConversation({...item, status: Number(item.status)}))
        .filter(item => item.isPinned);
      if (!pinnedItems.length) return;

      this.mergeAndAppend(pinnedItems);
      this.recountUnread();
    } catch (error) {
      this.logger.debug('[ConversationList] Cannot scan pinned conversations:', error);
    }
  }

  private toTimestamp(value: unknown): number {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric < 999999999999 ? numeric * 1000 : numeric;
    }
    const parsed = new Date(String(value || 0)).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toBooleanFlag(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  /**
   * Opening/reading a conversation only changes its badge, not its position (FE13).
   * Apply the same rule to local updates and socket echoes: viewer, typing, tags,
   * avatar... must not indirectly sort a just-read conversation to the bottom.
   * Real activity, pin changes and explicit unread changes still update ordering.
   */
  private shouldReorderConversation(previous: IConversation, next: IConversation, hasMessages = false): boolean {
    if (hasMessages) return true;
    if (!!previous.isPinned !== !!next.isPinned
      || this.toTimestamp(previous.pinnedAt) !== this.toTimestamp(next.pinnedAt)) return true;
    if (this.toTimestamp(previous.updatedTime) !== this.toTimestamp(next.updatedTime)
      || this.toTimestamp(previous.lastSentMessageTime) !== this.toTimestamp(next.lastSentMessageTime)) return true;
    if (this._sortMode === ESortMode.NO_REPLY_FIRST && !!previous.hasReply !== !!next.hasReply) return true;
    return this._sortMode === ESortMode.UNREAD_FIRST
      && Number(next.status) === CONVERSATION_STATUS.UNREAD
      && Number(previous.status) !== Number(next.status);
  }

  /**
   * Sắp xếp danh sách theo 3 kiểu:
   * 1. UNREAD_FIRST: Pin → Chưa đọc trước → Thời gian
   * 2. BY_TIME: Pin → Thời gian mới nhất
   * 3. NO_REPLY_FIRST: Pin → Chưa trả lời trước → Thời gian
   */
  private sortItems(): void {
    const current = this._items$.getValue();
    if (!current.length) return;

    // [FIX] Tạo new reference để async pipe luôn detect change — tránh trường hợp
    // Block A emit rồi sortItems emit cùng ref → async pipe skip emission
    const sorted = [...current];

    sorted.sort((a, b) => {
      // Cấp 1: Ưu tiên ghim (pin)
      const pin = this.comparePinned(a, b);
      if (pin !== 0) return pin;

      // Cấp 2: Tùy theo kiểu sort
      switch (this._sortMode) {
        case ESortMode.UNREAD_FIRST: {
          // Chưa đọc (status=1) lên trước đã đọc (status=2)
          if (a.status < b.status) return -1;
          if (a.status > b.status) return 1;
          // Cùng trạng thái → thời gian mới nhất lên trước
          return (b.updatedTime || b.lastSentMessageTime || 0) - (a.updatedTime || a.lastSentMessageTime || 0);
        }
        case ESortMode.NO_REPLY_FIRST: {
          // Chưa trả lời lên trước đã trả lời
          if (!a.hasReply && b.hasReply) return -1;
          if (a.hasReply && !b.hasReply) return 1;
          // Cùng trạng thái → thời gian mới nhất lên trước
          return (b.updatedTime || b.lastSentMessageTime || 0) - (a.updatedTime || a.lastSentMessageTime || 0);
        }
        case ESortMode.BY_TIME:
        default: {
          // Chỉ sort theo thời gian mới nhất
          return (b.updatedTime || b.lastSentMessageTime || 0) - (a.updatedTime || a.lastSentMessageTime || 0);
        }
      }
    });

    this._items$.next(sorted);
  }

  // ===========================================================================
  // SECTION 4: ĐẾM UNREAD + CẬP NHẬT TITLE TAB
  // ===========================================================================

  /**
   * Đếm lại số hội thoại chưa đọc và phát ra alertUnread$
   */
  public recountUnread(): void {
    // Reset base title để lấy lại title hiện tại (phòng user đã navigate sang trang khác)
    this._baseTitle = null;

    const count = this._items$.getValue().filter(c => c.status === CONVERSATION_STATUS.UNREAD).length;
    this._alertUnread$.next(count);
    this.updateBrowserTitle(count);
  }

  /**
   * Cập nhật title tab trình duyệt hiển thị số tin chưa đọc.
   * Luôn ghi đè (override), không cộng dồn prefix.
   * VD: "(5) VPage" nếu có 5 tin chưa đọc, "VPage" nếu không có.
   */
  private updateBrowserTitle(count: number): void {
    if (typeof document === 'undefined') return;

    // Lưu base title lần đầu (xoá prefix unread cũ nếu có)
    if (this._baseTitle === null) {
      this._baseTitle = this.titleService.getTitle().replace(/^\(\d+\)\s+/, '');
    }

    // Luôn ghi đè từ base title — không đọc document.title (tránh cộng dồn)
    const newTitle = count > 0 ? `(${count}) ${this._baseTitle}` : this._baseTitle;

    this.titleService.setTitle(newTitle);
  }

  // ===========================================================================
  // SECTION 5: THỰC THI HÀNH ĐỘNG (PIN / MUTE / MARK_UNREAD...)
  // ===========================================================================

  /**
   * Thực thi các hành động nghiệp vụ trên 1 hội thoại.
   * Sử dụng Optimistic Update + Rollback nếu API lỗi.
   * Response format: TẤT CẢ đều theo IResponseApi<T> (res.code === 1 = thành công)
   */
  public async executeAction(event: IConversationActionEvent): Promise<void> {
    const {action, conversationId, payload} = event;
    const previous = this.getCurrentItems().find(item => item.id === conversationId);
    if (!previous) return;

    try {
      switch (action) {
        case EConversationAction.PIN: {
          // Optimistic: phản hồi UI ngay trước khi chờ backend
          const pinnedAt = Date.now();
          this.updateConversationInList(conversationId, {isPinned: true, pinnedAt});
          const pinParams = {id: conversationId, conversationId, pageId: previous.fanpageId};
          const res = await firstValueFrom(this.api.handleConversationAction('pinConversation', pinParams));
          if (res?.code !== 1) {
            // Rollback nếu backend từ chối
            this.updateConversationInList(conversationId, {isPinned: previous.isPinned, pinnedAt: previous.pinnedAt});
          } else {
            this.emitPinStateChange(previous.fanpageId, conversationId, true, pinnedAt);
          }
          break;
        }
        case EConversationAction.UNPIN: {
          this.updateConversationInList(conversationId, {isPinned: false, pinnedAt: undefined});
          const unpinParams = {id: conversationId, conversationId, pageId: previous.fanpageId};
          const res = await firstValueFrom(this.api.handleConversationAction('unpinConversation', unpinParams));
          if (res?.code !== 1) {
            this.updateConversationInList(conversationId, {isPinned: previous.isPinned, pinnedAt: previous.pinnedAt});
          } else {
            this.emitPinStateChange(previous.fanpageId, conversationId, false);
          }
          break;
        }
        case EConversationAction.MARK_UNREAD: {
          // Đánh dấu chưa đọc: countUnread tối thiểu 1
          this.updateConversationInList(conversationId, {
            countUnread: Math.max(previous.countUnread || 0, 1),
            status: CONVERSATION_STATUS.UNREAD
          });
          const res = await firstValueFrom(this.api.updateConversation({
            id: conversationId,
            status: CONVERSATION_STATUS.UNREAD
          }));
          if (res?.code !== 1) {
            this.updateConversationInList(conversationId, {
              countUnread: previous.countUnread,
              status: previous.status
            });
          }
          // Đếm lại sau khi mark
          this.recountUnread();
          break;
        }
        case EConversationAction.MUTE: {
          const muteType = payload?.type as MuteTypeValue | undefined;
          if (!muteType) {
            this.logger.warn('[ConversationList] MUTE thiếu `payload.type`.');
            break;
          }
          // Optimistic update trước khi gọi API.
          this.updateConversationInList(conversationId, {
            isMuted: true,
            muteType: muteType as IConversation['muteType']
          });
          try {
            const result = await this.muteService.mute(
              previous.fanpageId, conversationId, muteType,
            );
            // muteUntil có thể null với MUTE_FOREVER
            this.updateConversationInList(conversationId, {
              isMuted: true,
              muteType: muteType as IConversation['muteType'],
              muteUntilTimestamp: result.muteUntil ?? undefined
            });
          } catch {
            this.updateConversationInList(conversationId, {
              isMuted: previous.isMuted,
              muteType: previous.muteType,
              muteUntilTimestamp: previous.muteUntilTimestamp
            });
          }
          break;
        }
        case EConversationAction.UNMUTE: {
          this.updateConversationInList(conversationId, {
            isMuted: false,
            muteUntilTimestamp: undefined,
            muteType: undefined
          });
          try {
            await this.muteService.unmute(previous.fanpageId, conversationId);
          } catch {
            this.updateConversationInList(conversationId, {
              isMuted: previous.isMuted,
              muteType: previous.muteType,
              muteUntilTimestamp: previous.muteUntilTimestamp
            });
          }
          break;
        }
        case EConversationAction.SELECT: {
          // Chỉ việc phát tín hiệu chọn đi, ai cần (như ChatCore) sẽ tự nghe và xử lý
          if (previous) {
            this.interactionService.emitSelect(
              conversationId,
              previous.fanpageId,
              previous.channel,
              previous
            );
          }
          break;
        }
        case EConversationAction.SHARE: {
          // Mở dialog chia sẻ hội thoại — dialog thực tế sẽ được xử lý ở Phase 4
          this.interactionService.emitOpenShare(conversationId, previous);
          break;
        }
        case EConversationAction.ASSIGN_STAFF: {
          // Mở dialog gán nhân viên — dialog thực tế sẽ được xử lý ở Phase 4
          this.interactionService.emitOpenAssignStaff(conversationId, previous);
          break;
        }
        case EConversationAction.CREATE_REMINDER: {
          // Mở dialog tạo lịch hẹn — port từ FE13 onCreateReminder()
          const businessId = this.authService.getActiveBusiness()?.id ?? null;
          this.interactionService.emitOpenCreateReminder(conversationId, previous.fanpageId, businessId);
          break;
        }
        default:
          this.logger.warn(`[ConversationList] Hành động ${action} chưa được handle.`);
          break;
      }
    } catch (err) {
      // Rollback toàn bộ nếu có exception
      this.updateConversationInList(conversationId, previous);
      this.logger.error('[ConversationList] executeAction lỗi:', err);
    }
  }

  private emitPinStateChange(
    pageId: string,
    conversationId: string,
    isPinned: boolean,
    pinnedAt?: number,
  ): void {
    this.realtime.emitConversationChanges(pageId, {
      conversation: {
        id: conversationId,
        fanpageId: pageId,
        isPinned,
        pinnedAt: isPinned ? pinnedAt : null,
      } as unknown as IConversation,
    });
  }
}
